import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import "./crm-lite.css";
import "./common-view.css";
import ClientSearch from "./client-search";
import useAuthSession from "../hooks/useAuthSession";
import { BACKEND_BASE_URL } from "../config/appUrls";
import { hasValidPlatformAccess } from "../utils/auth";

export default function CrmLite() {
  const { t, i18n } = useTranslation();
  const auth = useAuthSession();
  const { token, clientId, language } = auth;
  const [selectedClient, setSelectedClient] = useState(null);
  const [targetClientId, setTargetClientId] = useState(clientId || "");

  const [details, setDetails] = useState({ leads: [], conversations: [] });
  const [activeView, setActiveView] = useState("new");
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [showConversation, setShowConversation] = useState(false);

  const currentLocale = useMemo(() => {
    const lang = (i18n.language || language || "en").toLowerCase();
    return lang.startsWith("es") ? "es-ES" : "en-US";
  }, [i18n.language, language]);

  const handleClientSelect = useCallback(client => {
    setSelectedClient(client);
    setTargetClientId(client.client_id);
    setSelectedConversation(null);
    setShowConversation(false);
  }, []);

  const hasPlatformAccess = useMemo(() => {
    return hasValidPlatformAccess(selectedClient);
  }, [selectedClient]);

  useEffect(() => {
    if (!selectedClient) return;
    if (!hasPlatformAccess) {
      alert(t("dashboard.subscriptionRequiredDesc"));
      window.location.href = "/dashboard?tab=onboarding";
    }
  }, [hasPlatformAccess, selectedClient, t]);

  useEffect(() => {
    if (!token || !targetClientId) return;

    setSelectedConversation(null);
    setShowConversation(false);
    loadClientDetails(targetClientId);
  }, [token, targetClientId]);

  async function loadClientDetails(clientId) {
    try {
      const res = await fetch(`${BACKEND_BASE_URL}/api/crm-lite/details`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ client_id: clientId })
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || t("crm-lite.errors.loadDetails"));

      setDetails({
        leads: Array.isArray(json.leads) ? json.leads : [],
        conversations: Array.isArray(json.conversations) ? json.conversations : []
      });
    } catch (e) {
      console.error(e);
      setDetails({ leads: [], conversations: [] });
    }
  }

  const leadsByStatus = useMemo(() => {
    const groups = { new: [], contacted: [], qualified: [], lost: [] };
    const sortedLeads = [...details.leads].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );

    sortedLeads.forEach(l => {
      const s = (l.status || "new").toLowerCase();
      if (groups[s]) groups[s].push(l);
      else groups.new.push(l);
    });
    return groups;
  }, [details.leads]);

  const conversationsByCustomer = useMemo(() => {
    const map = {};
    details.conversations.forEach(m => {
      const key = m.phone || "unknown";
      if (!map[key]) map[key] = [];
      map[key].push(m);
    });
    return Object.entries(map)
      .map(([phone, msgs]) => ({
        phone,
        lastMessage: msgs[msgs.length - 1],
        count: msgs.length
      }))
      .sort(
        (a, b) => new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at)
      );
  }, [details.conversations]);

  const summary = useMemo(() => {
    const newLeads = leadsByStatus.new.length;
    const hotLeads = details.leads.filter(l => (l.status || "").toLowerCase() === "qualified").length;
    const noResponse = details.leads.filter(l => (l.status || "").toLowerCase() === "contacted").length;
    const lostLeads = details.leads.filter(l => (l.status || "").toLowerCase() === "lost").length;

    return { newLeads, hotLeads, noResponse, lostLeads };
  }, [leadsByStatus, details.leads]);

  function formatTimeAgo(dateStr) {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    const weeks = Math.floor(diff / (86400000 * 7));
    const months = Math.floor(diff / (86400000 * 30));

    if (minutes < 1) return t("crm-lite.conversation.time.justNow");
    if (minutes < 60) return t("crm-lite.conversation.time.minAgo", { count: minutes });
    if (hours < 24) return t("crm-lite.conversation.time.hrAgo", { count: hours });
    if (days < 7) return t(days === 1 ? "crm-lite.conversation.time.dayAgo" : "crm-lite.conversation.time.daysAgo", { count: days });
    if (weeks < 4) return t(weeks === 1 ? "crm-lite.conversation.time.weekAgo" : "crm-lite.conversation.time.weeksAgo", { count: weeks });
    return t(months === 1 ? "crm-lite.conversation.time.monthAgo" : "crm-lite.conversation.time.monthsAgo", { count: months });
  }

  function formatDateTime(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString(currentLocale, {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  }

  async function updateLeadStatus(leadId, status) {
    try {
      await fetch(`${BACKEND_BASE_URL}/api/crm-lite/lead-status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ leadId, status })
      });
      loadClientDetails(targetClientId);
    } catch (err) {
      console.error(err);
      alert(t("crm-lite.errors.loginRequired"));
    }
  }

  function getConversationForLead(lead) {
    return conversationsByCustomer.find(c => c.phone === lead.phone);
  }

  return (
    <div className="view-page crm-lite">
      <div className="view-card">
        <div className="view-header">
          <div>
            <h2 className="view-title">{t("crm-lite.title")}</h2>
            <p className="view-subtitle">{t("crm-lite.subtitle")}</p>
          </div>

          <div className="view-nav">
            <select
              className="view-dropdown"
              defaultValue=""
              onChange={e => {
                const route = e.target.value;
                if (route) window.location.href = route;
              }}
            >
              <option value="" disabled>{t("dropDown.crm-lite-page")}</option>
              <option value="/">{t("dropDown.home-page")}</option>
              <option value="/support">{t("dropDown.support-page")}</option>
              <option value="/qr-code">{t("dropDown.qr-page")}</option>
              <option value="/dashboard">{t("dropDown.dashboard-page")}</option>
              <option value="/clients">{t("dropDown.clients-page")}</option>
              <option value="/scheduler">{t("dropDown.scheduler-page")}</option>
              <option value="/commerce">{t("dropDown.commerce-page")}</option>
              <option value="/dispatcher">{t("dropDown.dispatcher-page")}</option>
              <option value="/analytics">{t("dropDown.analytics-page")}</option>
              <option value="/tutorial">{t("dropDown.tutorial-page")}</option>
            </select>
          </div>
        </div>

        <ClientSearch onSelect={handleClientSelect} />

        {targetClientId && (
          <div className="card">
            <div className="card-title">
              {t("crm-lite.selectedClient")}:{" "}
              {selectedClient
                ? selectedClient.business_name || selectedClient.owner_name
                : t("crm-lite.yourBusiness", "Your Business")}
            </div>

            <div className="crm-section">
              <div className="crm-section-title">{t("crm-lite.summary.title")}</div>
              <div className="crm-summary-grid">
                <div
                  className={`crm-summary-card crm-card-new ${activeView === "new" ? "active" : ""}`}
                  onClick={() => {
                    setActiveView("new");
                    setSelectedConversation(null);
                    setShowConversation(false);
                  }}
                >
                  <div className="crm-card-val">🔥 {summary.newLeads}</div>
                  <span className="crm-card-label">{t("crm-lite.summary.newLeads")}</span>
                  <div className="crm-hint">{t("crm-lite.summary.followUp")}</div>
                </div>

                <div
                  className={`crm-summary-card crm-card-hot ${activeView === "qualified" ? "active" : ""}`}
                  onClick={() => {
                    setActiveView("qualified");
                    setSelectedConversation(null);
                    setShowConversation(false);
                  }}
                >
                  <div className="crm-card-val">⭐ {summary.hotLeads}</div>
                  <span className="crm-card-label">{t("crm-lite.summary.hotLeads")}</span>
                  <div className="crm-hint">{t("crm-lite.summary.highInterest")}</div>
                </div>

                <div
                  className={`crm-summary-card crm-card-warm ${activeView === "contacted" ? "active" : ""}`}
                  onClick={() => {
                    setActiveView("contacted");
                    setSelectedConversation(null);
                    setShowConversation(false);
                  }}
                >
                  <div className="crm-card-val">📉 {summary.noResponse}</div>
                  <span className="crm-card-label">{t("crm-lite.summary.noResponse")}</span>
                  <div className="crm-hint">{t("crm-lite.summary.reengage")}</div>
                </div>

                <div
                  className={`crm-summary-card crm-card-lost ${activeView === "lost" ? "active" : ""}`}
                  onClick={() => {
                    setActiveView("lost");
                    setSelectedConversation(null);
                    setShowConversation(false);
                  }}
                >
                  <div className="crm-card-val">❌ {summary.lostLeads}</div>
                  <span className="crm-card-label">{t("crm-lite.summary.lostLeads")}</span>
                  <div className="crm-hint">{t("crm-lite.summary.noConversion")}</div>
                </div>
              </div>
            </div>

            <div className="crm-section">
              <div className="crm-section-title">
                {activeView === "conversations"
                  ? `💬 ${t("crm-lite.conversation.title")}`
                  : `${t(`crm-lite.summary.${activeView === "new" ? "newLeads" : activeView === "qualified" ? "hotLeads" : activeView === "contacted" ? "noResponse" : "lostLeads"}`).toUpperCase()} - ${t("crm-lite.leads.title")}`}
              </div>

              {activeView !== "conversations" &&
                leadsByStatus[activeView]?.map(l => {
                  const convo = getConversationForLead(l);
                  let urgencyClass = "";

                  if (convo) {
                    const ageMinutes = (Date.now() - new Date(convo.lastMessage.created_at)) / 60000;
                    urgencyClass = ageMinutes < 10 ? "hot" : ageMinutes < 60 ? "warm" : "cold";
                  }

                  return (
                    <div key={l.lead_id} className="crm-row">
                      <div className="crm-lead-box" onClick={() => setSelectedLead(l)}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <div className="crm-lead-name">👤 {l.name}</div>
                          <div style={{ textAlign: "right", fontSize: "12px", color: "#9ca3af", lineHeight: "1.4" }}>
                            <div>{t("commonLabel.created", "Created")}: {formatDateTime(l.created_at)}</div>
                            <div>{t("commonLabel.updated", "Updated")}: {formatDateTime(l.updated_at)}</div>
                          </div>
                        </div>

                        <div className="crm-lead-contact">
                          📞 {l.phone}
                          {l.source === "whatsapp" && (
                            <a
                              href={`https://wa.me/${l.phone}`}
                              target="_blank"
                              rel="noreferrer"
                              className="crm-whatsapp-btn"
                            >
                              WhatsApp
                            </a>
                          )}
                        </div>

                        <div>📍 {t("crm-lite.leads.source")}: {l.source}</div>
                        <div
                          className={`crm-interest ${
                            l.interest === "high" ? "high" : l.interest === "medium" ? "medium" : "low"
                          }`}
                        >
                          {l.interest === "high" && "🔥"}
                          {l.interest === "medium" && "⚡"}
                          {l.interest === "low" && "🧊"}{" "}
                          {t("crm-lite.leads.interest")}:{" "}
                          {t(`crm-lite.interestLevel.${l.interest}`, l.interest || "medium")}
                        </div>

                        <div className="view-actions">
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              updateLeadStatus(l.lead_id, "contacted");
                            }}
                          >
                            {t("crm-lite.leads.contacted")}
                          </button>

                          <button
                            onClick={e => {
                              e.stopPropagation();
                              updateLeadStatus(l.lead_id, "qualified");
                            }}
                          >
                            {t("crm-lite.leads.qualified")}
                          </button>

                          <button
                            onClick={e => {
                              e.stopPropagation();
                              updateLeadStatus(l.lead_id, "lost");
                            }}
                          >
                            {t("crm-lite.leads.lost", "Lost")}
                          </button>
                        </div>
                      </div>

                      <div
                        className={`crm-convo-preview ${urgencyClass}`}
                        onClick={() => {
                          setSelectedConversation(convo);
                          setShowConversation(true);
                        }}
                      >
                        {convo ? (
                          <>
                            <div className="crm-time">
                              {convo.lastMessage.direction === "inbound"
                                ? `👤 ${t("crm-lite.conversation.customer")}`
                                : `🤖 ${t("crm-lite.conversation.bot")}`}
                              {" • "}
                              ({formatTimeAgo(convo.lastMessage.created_at)})
                            </div>
                            <div
                              className={`crm-preview-bubble ${
                                convo.lastMessage.direction === "inbound" ? "inbound" : "outbound"
                              }`}
                            >
                              <div className="crm-preview-text">
                                {convo.lastMessage.message}
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="muted small">
                            {t("crm-lite.conversation.noConversation")}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>

            {showConversation && selectedConversation && (
              <div className="crm-convo-full">
                <div className="crm-convo-header">
                  <div className="crm-section-title">
                    💬 {t("crm-lite.conversation.history")}
                  </div>
                  <button className="crm-close-btn" onClick={() => setShowConversation(false)}>
                    ✖
                  </button>
                </div>

                {details.conversations
                  .filter(m => m.phone === selectedConversation.phone)
                  .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
                  .map(m => (
                    <div
                      key={m.message_id}
                      className={`crm-message-row ${
                        m.direction === "inbound" ? "inbound" : "outbound"
                      }`}
                    >
                      <div className="crm-bubble">
                        <div className="crm-bubble-text">{m.message}</div>
                        <div className="crm-bubble-time">
                          {m.direction === "inbound"
                            ? `👤 ${t("crm-lite.conversation.customer")}`
                            : `🤖 ${t("crm-lite.conversation.bot")}`}{" "}
                          {formatTimeAgo(m.created_at)}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}